import {
  UC_PAYG_RATE_USD,
  UC_TIER_IDS,
  UC_TIERS,
  POSTED_PRICE_TABLE,
  type PostedRungId,
  type PricingTierId,
} from '@syncanix/shared';

/**
 * Pure UC math for the marketing usage calculator, on the **posted-price
 * interaction model** (ADR-0442, repriced by ADR-0776).
 *
 * A price is not measured — it is one of exactly four posted values, decided by
 * what the interaction DID:
 *
 *      6  the assistant answered
 *     15  it answered and took an action
 *     18  it answered and looked something up
 *     30  it did both
 *
 * So there is no interaction weight left to estimate around, and this module
 * carries no workload presets and no token component. A buyer states how many
 * interactions they expect and what share of them act / look something up, and
 * gets an EXACT usage-credit total — the same menu the meter charges, not a
 * calibration of it.
 *
 * **The menu is a table, so a mix has to be resolved into CELLS.** Under the
 * superseded additive ladder a share could be priced on its own — every
 * interaction paid the base, the acting share added one increment, the
 * looking-up share added another, and the two never had to meet. ADR-0776's
 * `both` cell is 30 rather than the additive 27, so a mix is only priced once
 * you know how many interactions land in each of the four cells, and two
 * independent shares do not determine that on their own. {@link estimateMix}
 * assumes the two behaviours are INDEPENDENT and clamps the overlap to the
 * range the marginals actually permit (the Fréchet bounds) — see its own note.
 *
 * No I/O, no React, no clock. Every constant traces to the pricing SOT
 * (`@syncanix/shared`): the posted menu, the UC allowances and the PAYG overage
 * rate are imported, never re-typed here.
 */

/**
 * How many interactions a usage-credit allowance buys, at both ends of the
 * posted menu — re-exported from the pricing SOT rather than computed here.
 *
 * It briefly lived in this file, because `apps/marketing` and `apps/web` may not
 * import each other and only the tier cards needed it. The dashboard's
 * allowance-gauge caption then needed the same figure, which is precisely the F6
 * duplication the interaction-model audit documented
 * (`docs/pricing-interaction-model/README.md`): a published number in two places
 * has two chances to be wrong and no single place to check it. It now lives in
 * `packages/shared/src/pricing/posted-price.ts` beside `postedInteractionPrice`,
 * and both apps read it from there.
 *
 * Re-exported (rather than importing `@syncanix/shared` at each call site) so
 * this feature's public surface stays one import for its consumers.
 */
export { postedInteractionsForUsageCredits, type PostedInteractionRange } from '@syncanix/shared';

/**
 * What a buyer tells the calculator: their expected monthly interaction volume,
 * and what share of those interactions reach each of the two priced rungs.
 *
 * Shares are independent, exactly as the flags are — an interaction can act, or
 * look something up, or both, or neither — so they do not sum to 100.
 */
export interface InteractionMix {
  readonly interactions: number;
  /** Percent (0–100) of interactions in which the assistant takes an action. */
  readonly actedPercent: number;
  /** Percent (0–100) of interactions in which it looks something up. */
  readonly lookedUpPercent: number;
}

/** The priced breakdown of an {@link InteractionMix} — every figure a whole number. */
export interface MixEstimate {
  readonly interactions: number;
  /** Interactions that act — the MARGINAL total, `cells.acted + cells.both`. */
  readonly actedInteractions: number;
  /** Interactions that look something up — `cells.lookedUp + cells.both`. */
  readonly lookedUpInteractions: number;
  /**
   * How many interactions land in each cell of the posted menu. Sums to
   * {@link MixEstimate.interactions}; this is what the total is priced from,
   * and what the result copy names so a buyer can check the arithmetic.
   */
  readonly cells: Readonly<Record<PostedRungId, number>>;
  readonly usageCredits: number;
}

/** Coerce a raw slider/input value into a clean, non-negative integer count. */
export function normalizeInteractions(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.floor(value);
}

/** Coerce a raw share slider value into a whole percent inside 0–100. */
export function normalizeSharePercent(value: number): number {
  if (Number.isNaN(value)) return 0;
  if (value <= 0) return 0;
  if (value >= 100) return 100;
  return Math.round(value);
}

/**
 * The exact usage credits an interaction mix draws in a month: the two shares
 * are resolved into the four cells of {@link POSTED_PRICE_TABLE}, and each cell
 * is charged its posted price. Counts are whole interactions, so the total is a
 * whole number of credits and is bounded by 6×N … 30×N by construction.
 *
 * **The overlap is an assumption, and it is stated rather than hidden.** A buyer
 * supplies two marginals — how many interactions act, how many look something up
 * — and those do not determine how many do BOTH, which is the cell the posted
 * menu prices highest. The estimate assumes the two behaviours are INDEPENDENT
 * (`N × acted × lookedUp`), then clamps that to the range the marginals actually
 * permit:
 *
 *   - never above `min(acted, lookedUp)` — the overlap cannot exceed either side;
 *   - never below `acted + lookedUp − N` — with more of both than there are
 *     interactions, some MUST overlap.
 *
 * Those are the Fréchet bounds, and the clamp is what keeps every cell
 * non-negative: at 100 % / 100 % the lower bound forces the whole month into
 * `both` rather than leaving a negative `answer` count. Independence is the
 * neutral assumption, not a measured one — a workload where acting and looking
 * up are correlated pays more than this estimate, and one where they are
 * mutually exclusive pays less. Both stay inside the published band, which is
 * the promise the calculator actually makes.
 */
export function estimateMix(mix: InteractionMix): MixEstimate {
  const interactions = normalizeInteractions(mix.interactions);
  const actedShare = normalizeSharePercent(mix.actedPercent) / 100;
  const lookedUpShare = normalizeSharePercent(mix.lookedUpPercent) / 100;
  const actedInteractions = Math.round(interactions * actedShare);
  const lookedUpInteractions = Math.round(interactions * lookedUpShare);

  const minOverlap = Math.max(0, actedInteractions + lookedUpInteractions - interactions);
  const maxOverlap = Math.min(actedInteractions, lookedUpInteractions);
  const both = Math.min(
    maxOverlap,
    Math.max(minOverlap, Math.round(interactions * actedShare * lookedUpShare)),
  );

  const cells: Record<PostedRungId, number> = {
    answer: interactions - actedInteractions - lookedUpInteractions + both,
    acted: actedInteractions - both,
    lookedUp: lookedUpInteractions - both,
    both,
  };

  let usageCredits = 0;
  for (const rung of Object.keys(cells) as PostedRungId[]) {
    usageCredits += cells[rung] * POSTED_PRICE_TABLE[rung];
  }

  return { interactions, actedInteractions, lookedUpInteractions, cells, usageCredits };
}

/** The self-serve tiers, ascending by included UC (Enterprise is unbounded/custom). */
const SELF_SERVE_TIERS_ASC: readonly PricingTierId[] = UC_TIER_IDS.filter(
  (id) => id !== 'enterprise' && UC_TIERS[id].includedUsageCredits > 0,
).sort((a, b) => UC_TIERS[a].includedUsageCredits - UC_TIERS[b].includedUsageCredits);

/**
 * The smallest tier whose monthly included UC allowance covers `usageCredits`.
 * Returns `'enterprise'` when usage exceeds the largest self-serve allowance
 * (Scale) — a committed UC block, quoted per contract.
 */
export function recommendTier(usageCredits: number): PricingTierId {
  const uc = Math.max(0, usageCredits);
  for (const tier of SELF_SERVE_TIERS_ASC) {
    if (uc <= UC_TIERS[tier].includedUsageCredits) return tier;
  }
  return 'enterprise';
}

/** UC consumed beyond an allowance (0 when within it). */
export function overageUsageCredits(usageCredits: number, allowanceUc: number): number {
  return Math.max(0, usageCredits - allowanceUc);
}

/**
 * Cost of `overageUc` units of opt-in pay-as-you-go overage, at the shared PAYG
 * rate (`UC_PAYG_RATE_USD` = $0.012/UC). Zero when there is no overage.
 */
export function overageCostUsd(overageUc: number): number {
  return Math.max(0, overageUc) * UC_PAYG_RATE_USD;
}
